import React, { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@dashboard/components/ui/accordion';
import { Field } from '../Form';
import { renderFields, FormExtensionContext } from '../formUtils';
import { cn } from '@dashboard/lib/utils';

// `startClosed`: every row starts closed and several can be open, each showing its `status`.
function AccordionGroupField({
  items,
  startClosed = false,
  inputClassName,
  name,
  values,
  setFieldValue,
  errors,
  touched,
}) {
  const [currentOpenItem, setCurrentOpenItem] = useState(
    items.find(item => item.defaultOpen)?.value || (items.length > 0 ? items[0].value : undefined),
  );
  const {
    customFieldPropsAreEqual,
    customLeafFieldNames,
    customContainerChildrenPropsEqual,
    customContainerValuesEqual,
  } = React.useContext(FormExtensionContext);

  if (!items || !Array.isArray(items)) {
    console.warn('[AccordionGroupField] Invalid props. Expecting items array.', { items, name });
    return null;
  }

  const accordionProps = startClosed
    ? { type: 'multiple', defaultValue: [], className: 'w-full block border-t' }
    : {
        type: 'single',
        collapsible: true,
        className: 'w-full block',
        onValueChange: setCurrentOpenItem,
        defaultValue:
          items.find(item => item.defaultOpen)?.value ||
          (items.length > 0 ? items[0].value : undefined),
      };

  return (
    <Accordion {...accordionProps}>
      {items.map((item, index) => {
        if (!item.name || !item.label) {
          return null;
        }

        return (
          <AccordionItem key={item.name || index} value={item.name} className="last:border-b">
            {startClosed ? (
              <AccordionTrigger className="py-3 gap-2 items-center text-sm hover:no-underline">
                <span className="flex flex-1 justify-between gap-4">
                  <span>{item.label}</span>
                  <span className="font-normal text-gray-500 dark:text-muted-foreground">
                    {typeof item.status === 'function' ? item.status(values) : item.status}
                  </span>
                </span>
              </AccordionTrigger>
            ) : (
              <AccordionTrigger className="hover:no-underline">{item.label}</AccordionTrigger>
            )}

            <AccordionContent>
              <div className={startClosed ? '[&>*:last-child]:mb-0' : 'pt-2'}>
                {renderFields({
                  fields: item.fields,
                  values,
                  setFieldValue,
                }).map(field => (
                  <Field
                    key={field.name}
                    field={field}
                    values={values}
                    setFieldValue={setFieldValue}
                    inputClassName={inputClassName}
                    errors={errors}
                    touched={touched}
                    customFieldPropsAreEqual={customFieldPropsAreEqual}
                    customLeafFieldNames={customLeafFieldNames}
                    customContainerChildrenPropsEqual={customContainerChildrenPropsEqual}
                    customContainerValuesEqual={customContainerValuesEqual}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export default React.memo(AccordionGroupField);
